
interface ProcessTimelineProps {
  modelId: number;
}

export default function ProcessTimeline({ modelId }: ProcessTimelineProps) {
  const processSteps = [
    {
      title: 'Digital Design Created',
      description: 'Original 3D model designed with intricate details and optimized geometry',
      date: '2024-01-15',
      status: 'completed',
      icon: 'ri-pencil-ruler-2-line',
      details: [
        'High-resolution mesh created',
        'Surface details sculpted',
        'Geometry optimized for printing'
      ]
    },
    {
      title: 'Print Preparation',
      description: 'Model sliced and prepared with optimal settings for resin printing',
      date: '2024-01-16',
      status: 'completed',
      icon: 'ri-settings-3-line',
      details: [
        'Support structures generated',
        'Layer height: 0.05mm',
        'Print orientation optimized'
      ]
    },
    {
      title: '3D Printing Process',
      description: 'Layer-by-layer construction using high-quality resin material',
      date: '2024-01-17',
      status: 'completed',
      icon: 'ri-printer-line',
      details: [
        'Print time: 8 hours 45 minutes',
        'Material: Premium resin',
        'Quality monitoring throughout'
      ]
    },
    {
      title: 'Post-Processing',
      description: 'Support removal, cleaning, and UV curing for optimal finish',
      date: '2024-01-18',
      status: 'completed',
      icon: 'ri-brush-line',
      details: [
        'Supports carefully removed',
        'Surface cleaned and polished',
        'UV cured for durability'
      ]
    },
    {
      title: 'Quality Inspection',
      description: 'Thorough quality check to ensure perfect finish and details',
      date: '2024-01-19',
      status: 'completed',
      icon: 'ri-shield-check-line',
      details: [
        'Dimensional accuracy verified',
        'Surface quality inspected',
        'Detail preservation confirmed'
      ]
    },
    {
      title: 'Ready for Delivery',
      description: 'Model packaged and ready for shipment to customer',
      date: '2024-01-20',
      status: 'completed',
      icon: 'ri-gift-line',
      details: [
        'Protective packaging applied',
        'Quality certificate included',
        'Ready for shipment'
      ]
    }
  ];

  return (
    <section className="py-16 bg-gray-50 rounded-2xl mb-16">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Model Creation Journey</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Follow the complete transformation of this model from digital concept to finished product
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
            
            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative flex items-start gap-6">
                  {/* Timeline Dot */}
                  <div className={`relative z-10 w-16 h-16 flex items-center justify-center rounded-full border-4 ${
                    step.status === 'completed' 
                      ? 'bg-blue-600 border-blue-200' 
                      : 'bg-gray-300 border-gray-200'
                  }`}>
                    <i className={`${step.icon} text-2xl ${
                      step.status === 'completed' ? 'text-white' : 'text-gray-500'
                    }`}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">{step.date}</div>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                          step.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <i className={`${step.status === 'completed' ? 'ri-check-line' : 'ri-time-line'}`}></i>
                          {step.status === 'completed' ? 'Completed' : 'Pending'}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {step.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-check-line text-green-600"></i>
                          {detail}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}